// S8 — RUSSIAN for the go-live checklist (`onboarding.checklist.*`).
//
// Register notes:
//  · "Waive" is the load-bearing word on this screen and has no casual Russian
//    equivalent that keeps its weight. It is rendered as «отступление» /
//    «принять отступление» — the QMS term for a recorded, attributable decision
//    to accept a gap. «Пропустить» would read as "skip", which is exactly the
//    wrong idea for something signed against a named group admin.
//  · "Site" in the English item titles is the facility, so it follows
//    TERMINOLOGY_BASE.ru: «площадка».
//  · The `items.*` and `rule.*` key SHAPES are an API contract
//    (`detail.rule_key`) and are reproduced exactly.

import type { Translated } from "../../../../i18n-shape.js";
import type { checklistScreenEn } from "./checklist.i18n.js";

export const checklistScreenRu: Translated<typeof checklistScreenEn> = {
  items: {
    vertical: {
      pack_installed: { title: "Отраслевой пакет установлен" },
    },
    quality: {
      checks_reviewed: { title: "Проверки качества просмотрены" }, // D-214
    },
    catalogue: {
      products_min: { title: "Есть хотя бы один продукт" },
      boms_cover_products: { title: "Спецификации покрывают ваши продукты" },
      routings_cover_products: { title: "Маршруты покрывают ваши продукты" },
      routings_bound: { title: "Каждая операция маршрута указывает на реальный рабочий центр" },
    },
    plant: {
      facility_min: { title: "Есть хотя бы одна площадка" },
      departments_min: { title: "У каждой площадки есть подразделение" },
      work_centers_min: { title: "У каждого производственного подразделения есть рабочий центр" },
      machines_calibration: { title: "У калибруемых станков указана единица счётчика" },
    },
    people: {
      admin_per_facility: { title: "У каждой площадки есть администратор" },
      floor_workers_min: { title: "На каждой площадке с рабочими центрами есть рабочий цеха" },
      invites_ready: { title: "Ни одно приглашение не зависло" },
    },
    opening: {
      stock_entered: { title: "Начальные остатки внесены" },
      stock_valued: { title: "Начальные остатки оценены" },
      open_orders_entered: { title: "Открытые заказы на продажу внесены" },
      customers_entered: { title: "Клиенты внесены" },
    },
    channels: {
      qr_kit_printed: { title: "Комплект QR-этикеток напечатан" },
      floor_pwa_tested: { title: "Цеховое приложение проверено реальным сканированием" },
      notification_channel: { title: "Канал уведомлений подключён" },
    },
  },

  rule: {
    vertical: {
      pack_installed: "Должен быть установлен один отраслевой пакет.",
    },
    quality: {
      // D-214 — идентификатор решения остаётся здесь, не в тексте для клиента.
      checks_reviewed:
        "Шаг 5 должен быть подтверждён — установленные проверки качества и их допуски просмотрены.",
    },
    catalogue: {
      products_min: "Нужен хотя бы один продукт, не снятый с производства.",
      boms_cover_products:
        "Не менее 80% изготавливаемых вами продуктов должны иметь действующую спецификацию.",
      routings_cover_products:
        "Не менее 80% изготавливаемых вами продуктов должны иметь действующий маршрут.",
      routings_bound:
        "Каждая операция каждого действующего маршрута должна указывать на существующий рабочий центр.",
    },
    plant: {
      facility_min: "Нужна хотя бы одна площадка.",
      departments_min: "У каждой площадки нужно хотя бы одно подразделение.",
      work_centers_min:
        "Нужен хотя бы один рабочий центр, и он нужен каждому подразделению производственной площадки. Офисные и складские площадки не учитываются.",
      machines_calibration: "Каждому станку с учётом калибровки нужна единица счётчика.",
    },
    people: {
      admin_per_facility:
        "Каждой площадке нужен администратор — сотрудник или непринятое приглашение.",
      floor_workers_min:
        "Каждой площадке, где есть рабочие центры, нужен хотя бы один рабочий цеха.",
      invites_ready: "Ни одно приглашение не должно быть просроченным или недоставленным.",
    },
    opening: {
      stock_entered: "Внесите начальные остатки или примите отступление с указанием причины.",
      stock_valued: "Каждой строке начальных остатков нужна стоимость.",
      open_orders_entered:
        "Внесите открытые заказы на продажу или примите отступление с указанием причины.",
      customers_entered: "Нужен хотя бы один клиент.",
    },
    channels: {
      qr_kit_printed: "Напечатайте хотя бы один комплект QR-этикеток.",
      floor_pwa_tested:
        "Кто-то должен отсканировать QR-код поста и выполнить одну пробную операцию в цеховом приложении.",
      notification_channel: "Администратор подтверждает, что бот для сообщений подключён.",
    },
  },

  module: {
    "01_catalogue": "Каталог",
    "02_qr_kits": "Комплекты QR-этикеток",
    "04_production": "Производство",
    "06_inventory": "Склад",
    "08_maintenance": "Обслуживание",
    "09_sales": "Продажи и CRM",
    "10_procurement": "Закупки",
    "11_finance": "Финансы",
    "12_notifications": "Уведомления",
    "16_identity": "Сотрудники и площадки",
  },

  evidence: {
    count: "Найдено",
    required: "Требуется",
    active: "Действующих",
    discontinued: "Снятых с производства",
    total_rows: "Всего строк",
    installed_packs: "Установленных пакетов",
    pack_key: "Пакет",
    pack_version: "Версия пакета",
    manufactured_products: "Изготавливаемые продукты",
    with_active_bom: "С действующей спецификацией",
    with_active_routing: "С действующим маршрутом",
    coverage_pct: "Покрытие, %",
    required_pct: "Требуемое покрытие, %",
    active_routings: "Действующие маршруты",
    active_routing_steps: "Действующие операции маршрутов",
    referenced_work_centers: "Упомянутые рабочие центры",
    unbound_work_center_ids: "Отсутствующие рабочие центры",
    facilities: "Площадки",
    facilities_without_department: "Площадки без подразделений",
    active_work_centers: "Действующие рабочие центры",
    departments_without_work_center: "Подразделения без рабочего центра",
    non_production_departments_skipped: "Подразделения вне учёта (непроизводственные)",
    calibration_tracked_machines: "Станки с учётом калибровки",
    missing_meter_unit: "Без единицы счётчика",
    group_scope_admin: "Администратор на всю группу",
    facilities_without_admin: "Площадки без администратора",
    facilities_with_work_centers: "Площадки с рабочими центрами",
    group_scope_floor_worker: "Рабочий цеха на всю группу",
    facilities_without_floor_worker: "Площадки без рабочего цеха",
    stuck_invites: "Зависшие приглашения",
    invites_by_status: "Приглашения по статусам",
    delivery_failure_tracking: "Учёт сбоев доставки",
    rows: "Строк",
    rows_with_value: "Строк со стоимостью",
    ready_batches: "Готовых комплектов этикеток",
    test_operation_completed: "Пробная операция выполнена",
    attestation: "Подтверждение",
  },

  weight: "Вес {{weight}}",
  weightTotal: "Оценка из общего веса {{total}} по всем проверкам.",
  sectionWeight: "Набрано веса: {{earned}} из {{total}}",
  sectionItems: "Пунктов: {{n}}",
  manualItem: "Задаётся вручную",
  showDetail: "Показать подробности",
  hideDetail: "Скрыть подробности",
  evaluatedAt: "Проверено {{time}}",
  neverEvaluatedItem: "Ещё не проверялось.",
  noEvidence: "Подробностей по этой проверке пока не записано.",
  vacuous: "Проверять здесь пока нечего, поэтому проверка пройдена.",
  blockedOnModule: "Ожидает модуль {{module}} — выполнить эту проверку пока никто не может.",
  waivedReason: "Отступление: {{reason}}",

  emptyTitle: "Пока ничего не проверялось",
  emptyBody:
    "Это проверки, от которых зависит, можно ли вам запускаться. Запустите оценку, чтобы их заполнить.",
  emptyNoPermission: "Попросите администратора запустить оценку.",

  blockingTitle: "Остались блокирующие проверки",
  blockingBody:
    "Запуск невозможен, пока каждая из них не пройдена или по ней не принято отступление администратором группы.",

  actions: {
    evaluate: "Запустить оценку",
    evaluating: "Идёт проверка…",
    waive: "Принять отступление",
    waiving: "Оформляется…",
    waiveConfirm: "Принять отступление",
    unwaive: "Отменить отступление",
    attestPassed: "Отметить как выполненное",
    attestFailed: "Отметить как невыполненное",
    testScanOk: "Пробное сканирование прошло",
    cancel: "Отмена",
  },

  waiveTitle: "Принять отступление по этой проверке",
  waiveBody: "Укажите, почему этот пробел допустим. Причина записывается на ваше имя.",
  waiveBlockingBody:
    "Это блокирующая проверка, поэтому отступление может принять только администратор группы — и причина записывается на его имя.",
  waiveReasonLabel: "Причина",
  waiveReasonHint: "Не менее {{min}} символов. Введено: {{count}}.",

  attestUnavailable:
    "Это нельзя подтвердить в этой системе. Можно отметить как невыполненное или принять отступление.",

  goLiveHeading: "Запуск",
  preflight: {
    stepsDone: "Все шаги настройки ({{total}}) завершены.",
    stepsPending: "Эти шаги настройки не завершены: {{steps}}.",
    blockingClear: "Блокирующих проверок не осталось.",
    blockingUnmet: "Осталось блокирующих проверок: {{count}}.",
    scoreOk: "Полнота {{score}} при необходимых {{required}}.",
    scoreGap: "Полнота {{score}}; требуется {{required}} (не хватает {{gap}}).",
    serverEligible: "Все проверки подтверждают: завод готов к запуску.",
    serverNotEligible: "Запуск пока не разрешён.",
  },

  liveHistory: "Ваш завод запущен с показателем полноты {{score}}.",
  liveBy: "Запустил {{user}}.",

  errors: {
    loadTitle: "Не удалось загрузить чек-лист",
    loadDetail: "Проверки запуска прочитать не удалось. Попробуйте ещё раз.",
    blockingWaiveForbidden:
      "Принять отступление по блокирующей проверке может только администратор группы.",
    itemNotManual:
      "Эту проверку решает оценка, а не человек. Устраните то, что она требует, либо примите отступление с указанием причины.",
    checkNotAvailable: "Это нельзя подтвердить в этой системе.",
    alreadyLive: "Ваш завод уже запущен. Чек-лист сохраняется как запись.",
    busy: "Выполняется другое изменение настройки. Повторите через мгновение.",
    reasonTooShort: "Не принято. Проверьте причину и попробуйте ещё раз.",
    generic: "Действие не прошло. Попробуйте ещё раз.",
  },

  fixThis: "Исправить →",
  liveOn: "Ваш завод запущен {{date}} с показателем полноты {{score}}.",
};
